/**
 * Formula Engine for Salary Structure Calculations
 * Handles formula parsing, validation, and calculation
 */

class FormulaEngine {
  constructor() {
    this.functions = {
      SUM: (...args) => args.reduce((sum, val) => sum + (Number(val) || 0), 0),
      AVG: (...args) => {
        const validArgs = args.filter(val => !isNaN(Number(val)));
        return validArgs.length > 0 ? validArgs.reduce((sum, val) => sum + Number(val), 0) / validArgs.length : 0;
      },
      MAX: (...args) => Math.max(...args.map(val => Number(val) || 0)),
      MIN: (...args) => Math.min(...args.map(val => Number(val) || 0)),
      ROUND: (value, decimals = 0) => Math.round(Number(value) * Math.pow(10, decimals)) / Math.pow(10, decimals),
      CEIL: (value) => Math.ceil(Number(value) || 0),
      FLOOR: (value) => Math.floor(Number(value) || 0),
      ABS: (value) => Math.abs(Number(value) || 0),
      IF: (condition, trueValue, falseValue) => condition ? trueValue : falseValue,
      AND: (...args) => args.every(arg => Boolean(arg)),
      OR: (...args) => args.some(arg => Boolean(arg)),
      NOT: (value) => !value
    };

    this.operators = {
      '+': (a, b) => Number(a) + Number(b),
      '-': (a, b) => Number(a) - Number(b),
      '*': (a, b) => Number(a) * Number(b),
      '/': (a, b) => Number(b) !== 0 ? Number(a) / Number(b) : 0,
      '%': (a, b) => Number(b) !== 0 ? Number(a) % Number(b) : 0,
      '^': (a, b) => Math.pow(Number(a), Number(b)),
      '==': (a, b) => Number(a) === Number(b),
      '!=': (a, b) => Number(a) !== Number(b),
      '>': (a, b) => Number(a) > Number(b),
      '<': (a, b) => Number(a) < Number(b),
      '>=': (a, b) => Number(a) >= Number(b),
      '<=': (a, b) => Number(a) <= Number(b),
      '&&': (a, b) => Boolean(a) && Boolean(b),
      '||': (a, b) => Boolean(a) || Boolean(b)
    };
  }

  /**
   * Validate formula syntax
   * @param {string} formula
   * @param {Array} availableComponents - e.g. [{ id, name }] including CTC base component
   * @param {Object} statutoryRates
   */
  validateFormula(formula, availableComponents = [], statutoryRates = {}) {
    const errors = [];
    
    if (!formula || typeof formula !== 'string') {
      errors.push('Formula must be a non-empty string');
      return { isValid: false, errors };
    }

    // Check for balanced parentheses
    let openCount = 0;
    for (let char of formula) {
      if (char === '(') openCount++;
      if (char === ')') openCount--;
      if (openCount < 0) {
        errors.push('Unbalanced parentheses');
        break;
      }
    }
    if (openCount > 0) {
      errors.push('Unbalanced parentheses');
    }

    // Check for valid function names and variables (use availableComponents so CTC etc. are valid)
    const functionNames = Object.keys(this.functions);
    const usedFunctions = formula.match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    const invalidFunctions = usedFunctions.filter(func => 
      !functionNames.includes(func) && 
      !this.isValidVariable(func, availableComponents, statutoryRates)
    );
    
    if (invalidFunctions.length > 0) {
      errors.push(`Invalid function or variable: ${invalidFunctions.join(', ')}`);
    }

    // Check for valid operators
    const validOperators = Object.keys(this.operators);
    const formulaOperators = formula.match(/[+\-*/%^=!><&|]+/g) || [];
    const invalidOperators = formulaOperators.filter(op => !validOperators.includes(op));
    
    if (invalidOperators.length > 0) {
      errors.push(`Invalid operator: ${invalidOperators.join(', ')}`);
    }

    return { isValid: errors.length === 0, errors };
  }

  /**
   * Extract variables from formula
   */
  extractVariables(formula, availableComponents = [], statutoryRates = {}) {
    const variables = [];
    const componentNames = availableComponents.map(comp => comp.name);
    const statutoryNames = Object.keys(statutoryRates);
    
    // Find all potential variables (words that aren't functions or numbers)
    const words = formula.match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    
    words.forEach(word => {
      if (!Object.keys(this.functions).includes(word) && 
          (componentNames.includes(word) || statutoryNames.includes(word))) {
        if (!variables.includes(word)) {
          variables.push(word);
        }
      }
    });
    
    return variables;
  }

  /**
   * Check if a string is a valid variable name
   */
  isValidVariable(variable, availableComponents = [], statutoryRates = {}) {
    const componentNames = availableComponents.map(comp => comp.name);
    const statutoryNames = Object.keys(statutoryRates);
    return componentNames.includes(variable) || statutoryNames.includes(variable);
  }

  /**
   * Calculate formula result
   */
  calculateFormula(formula, variables = {}, context = {}) {
    try {
      // Replace variables with their values
      let processedFormula = formula;
      
      Object.keys(variables).forEach(variable => {
        const value = variables[variable];
        const regex = new RegExp(`\\b${variable}\\b`, 'g');
        processedFormula = processedFormula.replace(regex, value);
      });

      // Replace function calls with their results
      processedFormula = this.processFunctions(processedFormula);

      // Evaluate the mathematical expression
      const result = this.evaluateExpression(processedFormula);
      
      return {
        success: true,
        result: Number(result) || 0,
        processedFormula
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        result: null
      };
    }
  }

  /**
   * Process function calls in formula
   */
  processFunctions(formula) {
    let processedFormula = formula;
    
    // Process function calls (simplified approach)
    Object.keys(this.functions).forEach(funcName => {
      const regex = new RegExp(`${funcName}\\s*\\([^)]*\\)`, 'g');
      processedFormula = processedFormula.replace(regex, (match) => {
        try {
          // Extract arguments
          const argsMatch = match.match(/\(([^)]*)\)/);
          if (!argsMatch) return match;
          
          const args = argsMatch[1].split(',').map(arg => arg.trim());
          const result = this.functions[funcName](...args);
          return result.toString();
        } catch (error) {
          return match; // Return original if processing fails
        }
      });
    });
    
    return processedFormula;
  }

  /**
   * Evaluate mathematical expression safely
   */
  evaluateExpression(expression) {
    // Remove any remaining non-mathematical characters
    const cleanExpression = expression.replace(/[^0-9+\-*/().]/g, '');
    
    // Use Function constructor for safer evaluation than eval
    try {
      return new Function('return ' + cleanExpression)();
    } catch (error) {
      throw new Error('Invalid mathematical expression');
    }
  }

  /**
   * Generate mock data for testing formulas
   */
  generateMockData(components = [], statutoryRates = {}) {
    const mockData = {};
    
    // Generate mock component values
    components.forEach(component => {
      mockData[component.name] = Math.floor(Math.random() * 20000) + 5000; // 5000-25000 range
    });
    
    // Add statutory rates
    Object.keys(statutoryRates).forEach(rate => {
      mockData[rate] = statutoryRates[rate];
    });
    
    return mockData;
  }

  /**
   * Calculate salary structure totals
   */
  calculateStructureTotals(components, formulas = {}) {
    const totals = {
      earnings: 0,
      deductions: 0,
      benefits: 0,
      reimbursements: 0,
      gross: 0,
      net: 0
    };

    components.forEach(component => {
      let amount = 0;
      
      if (component.calculationType === 'formula' && component.selectedFormula) {
        // Calculate using formula
        const mockData = this.generateMockData(components);
        const calculation = this.calculateFormula(
          component.selectedFormula.formula,
          mockData
        );
        amount = calculation.success ? calculation.result : 0;
      } else {
        // Manual calculation (use formulaResult if available)
        amount = component.formulaResult || 0;
      }

      // Categorize amounts
      if (component.categories.earning) totals.earnings += amount;
      if (component.categories.deduction) totals.deductions += amount;
      if (component.categories.benefit) totals.benefits += amount;
      if (component.categories.reimbursement) totals.reimbursements += amount;
    });

    totals.gross = totals.earnings + totals.benefits + totals.reimbursements;
    totals.net = totals.gross - totals.deductions;

    return totals;
  }
}

// Create singleton instance
const formulaEngine = new FormulaEngine();

export default formulaEngine;
